import React, { useState, useEffect } from 'react';
import { Form, Modal, Button } from 'react-bootstrap';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { getAllReviewers } from '../../../../../../services/util/userManagement';
import styles from './InterviewInviteModal.module.scss';
import { CustomButton } from '../../../../../shared/customButton';
import { sendingInviteAditionalInterviewers } from '../../../../../../services/util/interviews';
import { toast } from 'react-toastify';

const InterviewInviteModal = (props) => {
    const [options, setOptions] = useState([]);
    const animatedComponents = makeAnimated();

    useEffect(() => {
        const setReviewers = async () => {
            const reviewersList = await getAllReviewers();

            // Convert to sets of emails for easy comparison
            const assignedReviewersEmails = new Set(props.assigned_reviewers.map(reviewer => reviewer.email));

            // Find unique reviewers by filtering out those already assigned
            const uniqueReviewers = reviewersList.data.filter(reviewer => 
                !assignedReviewersEmails.has(reviewer.user_id.email)
            );

            // Set the unique reviewers to options
            setOptions(uniqueReviewers);

        };
        setReviewers();
    }, [props.assigned_reviewers]);
    

    const validationSchema = Yup.object().shape({
        reviewers: Yup.array().min(1, 'At least one reviewer is required').max(3, 'You can select a maximum of 3 reviewers.').required('Required'),
    });

    const customStyles = (errors) => ({
        control: (base) => ({
            ...base,
            '&:hover': { borderColor: errors ? '#dc3545' : '#24859a' },
            border: `1px solid ${errors ? '#dc3545' : '#bdbdbd'}`,
            borderRadius: '6px',
            boxShadow: 'none',
        }),
        option: (styles, { isFocused, isSelected }) => ({
            ...styles,
            background: isSelected || isFocused ? 'rgba(45, 166, 192, 0.1)' : null,
            color: isSelected ? '#24859A' : '#24859A',
        }),
        multiValue: (styles) => ({
            ...styles,
            background: 'rgba(45, 166, 192, 0.1)',
        }),
        multiValueLabel: (styles) => ({
            ...styles,
            color: '#24859A',
        }),
        multiValueRemove: (styles) => ({
            ...styles,
            color: '#24859A',
            ':hover': {
                backgroundColor: '#24859A',
                color: 'white',
            },
        }),
    });

    const onSubmit = async (values) => {
        let  response = await sendingInviteAditionalInterviewers(props.sessionID,values.reviewers);
        if(response){
            if(response.success){
                toast.success(<div>Invitation Sent Successfully</div>, {
                    position: "top-right",
                    autoClose: 2500,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
            props.handleClose();
        }
        
    }

    return (
        <Modal
            show={props.showModal}
            onHide={props.handleClose}
            backdrop="static"
            keyboard={false}
            centered
            size="md"
        >
            <Modal.Body className={styles.modalBodyWrapper}>
                <div className={styles.modalCancelBtnDiv}>
                    <Button className={styles.modalCancelBtn} onClick={props.handleClose}>
                        <svg width="24" height="24" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13.5 4.5L4.5 13.5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M4.5 4.5L13.5 13.5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Button>
                </div>

                <div className={styles.modalHeaderDiv}>
                    <h2 className={styles.modalHeader}>Invite more Interviewers</h2>
                </div>

                <Formik
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={(values) => { onSubmit(values); }}
                    initialValues={{
                        reviewers: [],
                    }}
                >
                    {({
                        values,
                        errors,
                        setFieldValue,
                        handleSubmit,
                        setFieldError,
                    }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Form.Label className={styles.reviewerLabel}>
                                Reviewers
                            </Form.Label>
                            <p className={styles.reviewersNotice}>You can invite anyone who is already a user in this company account</p>
                            <Select
                                options={options}
                                isMulti
                                className={styles.selctText}
                                name="reviewers"
                                components={animatedComponents}
                                value={values.reviewers ? values.reviewers : []}
                                onChange={(selectedOptions) => {
                                    if (selectedOptions && selectedOptions.length > 3) {
                                        setFieldError('reviewers', 'You can select a maximum of 3 reviewers.');
                                        return;
                                    }
                                    setFieldValue('reviewers', selectedOptions);
                                }}
                                getOptionValue={(option) => `${option._id}`}
                                getOptionLabel={(option) => `${option.user_id.email} - ${option.user_id.first_name} ${option.user_id.last_name}`}
                                styles={customStyles(errors.reviewers)}
                            />
                            <p className={styles.errorMsg}>{errors.reviewers}</p>
                            <div className={styles.navigateBtnCol}>
                                <CustomButton
                                    buttonType="button"
                                    type="defaultBtn"
                                    label="Cancel"
                                    handleClick={props.handleClose}
                                />
                                <CustomButton
                                    buttonType="submit"
                                    type="primaryBtn"
                                    label="Send Invite"
                                    backicon={
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8.74987 10.0001H4.16653M4.09599 10.243L2.15022 16.0552C1.99736 16.5119 1.92093 16.7402 1.97578 16.8808C2.02341 17.0029 2.12571 17.0954 2.25195 17.1307C2.39731 17.1712 2.61687 17.0724 3.05598 16.8748L16.9822 10.608C17.4108 10.4151 17.6251 10.3187 17.6913 10.1847C17.7489 10.0684 17.7489 9.93179 17.6913 9.8154C17.6251 9.68143 17.4108 9.585 16.9822 9.39212L3.05112 3.12317C2.61334 2.92617 2.39444 2.82767 2.24922 2.86807C2.12311 2.90317 2.02082 2.99549 1.97302 3.11736C1.91798 3.25769 1.9936 3.4855 2.14482 3.94113L4.09653 9.82134C4.1225 9.8996 4.13549 9.93873 4.14062 9.97874C4.14517 10.0143 4.14512 10.0502 4.14048 10.0857C4.13525 10.1257 4.12216 10.1648 4.09599 10.243Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>                                        
                                    }
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </Modal.Body>
        </Modal>
    );
};

export default InterviewInviteModal;
